import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../api";
import { Async } from "../components/Async";
import type { AlertPolicy, Hub, Settings } from "../types";

export function SettingsPage() {
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const hubs = useQuery({ queryKey: ["hubs"], queryFn: api.hubs });

  return (
    <>
      <h1>Einstellungen</h1>
      <p className="lede">
        Diese Werte liegen in der Datenbank und wirken ab dem nächsten Lauf. Zugangsdaten stehen
        dagegen in der <code>.env</code> und sind hier bewusst nicht änderbar.
      </p>

      <h2>Alarmschwelle</h2>
      <Async query={settings}>{(data) => <AlertForm settings={data} />}</Async>

      <h2>Suche</h2>
      <Async query={settings}>{(data) => <SearchSummary settings={data} />}</Async>

      <h2>Stopover-Hubs</h2>
      <Async query={hubs}>{(rows) => <HubTable hubs={rows} />}</Async>
    </>
  );
}

function AlertForm({ settings }: { settings: Settings }) {
  const client = useQueryClient();
  const [draft, setDraft] = useState<AlertPolicy>(settings.alerts);
  const [saved, setSaved] = useState(false);

  // Nach einem Speichern liefert der Server den gueltigen Stand zurueck --
  // der Entwurf folgt ihm, statt eine eigene Wahrheit zu behalten.
  useEffect(() => setDraft(settings.alerts), [settings.alerts]);

  const save = useMutation({
    mutationFn: () => api.patchSettings({ alerts: draft }),
    onSuccess: async () => {
      setSaved(true);
      await client.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setSaved(false);
        save.mutate();
      }}
    >
      <div className="field">
        <label htmlFor="threshold">Schwelle in Euro (leer = keine)</label>
        <input
          id="threshold"
          type="number"
          min={1}
          value={draft.default_threshold_eur ?? ""}
          onChange={(event) =>
            setDraft({
              ...draft,
              default_threshold_eur: event.target.value === "" ? null : Number(event.target.value),
            })
          }
        />
      </div>

      <div className="field">
        <label htmlFor="cooldown">Ruhezeit zwischen zwei Meldungen (Stunden)</label>
        <input
          id="cooldown"
          type="number"
          min={1}
          value={draft.cooldown_hours}
          onChange={(event) => setDraft({ ...draft, cooldown_hours: Number(event.target.value) })}
        />
      </div>

      <div className="field">
        <label>
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
          />{" "}
          Alarme aktiv
        </label>
      </div>

      <button type="submit" disabled={save.isPending}>
        {save.isPending ? "Speichert …" : "Speichern"}
      </button>

      {save.isError && <div className="notice error">{String(save.error)}</div>}
      {saved && !save.isError && <div className="notice saved">Gespeichert.</div>}

      <p className="muted">
        Die Schwelle wird von Hand gesetzt und nicht aus der Historie abgeleitet. Min, P10 und
        Median unter <a href="/historie">Historie</a> sind die Entscheidungshilfe dafür.
      </p>
    </form>
  );
}

function SearchSummary({ settings }: { settings: Settings }) {
  const { search, rules } = settings;
  return (
    <div className="scroll">
      <table>
        <tbody>
          <tr>
            <th>Abflughäfen</th>
            <td>{search.origins.join(", ")}</td>
          </tr>
          <tr>
            <th>Ziel</th>
            <td>{search.destination}</td>
          </tr>
          <tr>
            <th>Suchfenster</th>
            <td>{search.search_window_months} Monate</td>
          </tr>
          <tr>
            <th>Reisedauer</th>
            <td>
              {search.min_trip_days} bis {search.max_trip_days} Tage
            </td>
          </tr>
          <tr>
            <th>Abtastraster</th>
            <td>
              alle {search.sample_stride_days} Tage, Aufenthalt{" "}
              {search.sample_trip_days.join(" und ")} Tage
            </td>
          </tr>
          <tr>
            <th>Reisezeit je Strecke</th>
            <td>höchstens {rules.max_leg_duration_minutes / 60} h</td>
          </tr>
          <tr>
            <th>Stopover</th>
            <td>
              höchstens {rules.max_stopover_minutes / 60} h, Ersparnis mindestens{" "}
              {Math.round(rules.stopover_min_savings_pct * 100)} %
            </td>
          </tr>
        </tbody>
      </table>
      <p className="muted">
        Diese Werte sind über die API änderbar (<code>PATCH /api/settings</code>). Ein Formular
        dafür fehlt noch – sie zu verstellen ändert den Umfang jedes Laufs und sollte bewusst
        geschehen.
      </p>
    </div>
  );
}

function HubTable({ hubs }: { hubs: Hub[] }) {
  const client = useQueryClient();
  const patch = useMutation({
    mutationFn: ({ iata, ...rest }: { iata: string; enabled?: boolean; min_layover_minutes?: number }) =>
      api.patchHub(iata, rest),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["hubs"] });
    },
  });

  return (
    <div className="scroll">
      <table>
        <thead>
          <tr>
            <th>Hub</th>
            <th>Visum</th>
            <th className="num">Mindestaufenthalt</th>
            <th>Offizielles Programm</th>
            <th>Aktiv</th>
          </tr>
        </thead>
        <tbody>
          {hubs.map((hub) => (
            <tr key={hub.iata}>
              <td>
                {hub.name} ({hub.iata})
              </td>
              <td className="muted">{hub.visa_status}</td>
              <td className="num">
                <input
                  type="number"
                  min={1}
                  step={30}
                  style={{ width: 90 }}
                  defaultValue={hub.min_layover_minutes}
                  onBlur={(event) => {
                    const value = Number(event.target.value);
                    if (value > 0 && value !== hub.min_layover_minutes) {
                      patch.mutate({ iata: hub.iata, min_layover_minutes: value });
                    }
                  }}
                />{" "}
                min
              </td>
              <td>
                {hub.has_official_program && hub.program_carriers.length > 0 ? (
                  <span className="badge ok">{hub.program_carriers.join(", ")}</span>
                ) : (
                  <span className="muted">–</span>
                )}
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={hub.enabled}
                  onChange={(event) =>
                    patch.mutate({ iata: hub.iata, enabled: event.target.checked })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {patch.isError && <div className="notice error">{String(patch.error)}</div>}
      <p className="muted">
        Ein abgeschalteter Hub trägt keine Stopover-Ausnahme mehr. Das offizielle Programm gilt nur
        bei der genannten Airline – ein Umstieg in Doha mit einer anderen bringt keinen
        Qatar-Stopover.
      </p>
    </div>
  );
}
